import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { http } from "../../lib/api";
import PremiumLayout from "./PremiumLayout";

const CATEGORIES = ["All", "Residential", "Interior", "Commercial", "Turnkey"];

export default function PremiumPortfolio() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    http.get("/marketing/site").then(({ data }) => setProjects(data.projects || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (filter === "All") return projects;
    return projects.filter((p) => (p.category || "").toLowerCase().includes(filter.toLowerCase()));
  }, [projects, filter]);

  // Asymmetric masonry: assign column spans + aspect ratios in a repeating rhythm
  const layout = (i) => {
    const patterns = [
      "md:col-span-3 aspect-[4/3]",
      "md:col-span-3 aspect-[4/3]",
      "md:col-span-4 aspect-[16/10]",
      "md:col-span-2 aspect-square",
      "md:col-span-2 aspect-square",
      "md:col-span-4 aspect-[16/10]",
    ];
    return patterns[i % patterns.length];
  };

  return (
    <PremiumLayout>
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 pb-8" data-testid="portfolio-header">
        <div className="mono gold">Portfolio</div>
        <h1 className="serif text-5xl md:text-7xl leading-[1.02] mt-4 cream">
          Ten years of <span className="gold italic">rooms, homes and streets.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-[#B8B0A0] text-lg leading-relaxed">
          A curated selection of civil, interior, and turnkey work. Each entry opens to an
          editorial case study with blueprints, material notes, and photography.
        </p>
      </section>

      {/* Filters */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 sticky top-[68px] z-30 bg-[#0A0A0A]/90 backdrop-blur border-y border-[#1F1F1F] py-4">
        <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setFilter(c)}
                    data-testid={`filter-${c.toLowerCase()}`}
                    className={`mono px-4 py-2 border transition-colors ${
                      filter === c
                        ? "bg-[#C9A96E] text-[#0A0A0A] border-[#C9A96E]"
                        : "border-[#2A2A2A] text-[#DDD5C4] hover:border-[#C9A96E] hover:text-[#C9A96E]"
                    }`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Uniform row-column grid — tight spacing per user spec */}
      <section className="max-w-[1600px] mx-auto px-3 md:px-4 py-8">
        {filtered.length === 0 ? (
          <div className="py-32 text-center text-[#7A7267]">No projects in this category yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 md:gap-2" data-testid="portfolio-grid">
            {filtered.map((p) => <PortfolioCard key={p.id} p={p} />)}
          </div>
        )}
      </section>
    </PremiumLayout>
  );
}

function PortfolioCard({ p }) {
  const cover = (p.photos || [])[0] || "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1400&auto=format&fit=crop&q=80";
  return (
    <Link to={`/projects/${p.id}`} data-testid={`portfolio-card-${p.id}`}
          className="group relative overflow-hidden aspect-[4/3]">
      <img src={cover} alt={p.title}
           className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
      {/* Hover overlay with readable title at bottom */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/95 via-[#0A0A0A]/20 to-transparent
                      opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 translate-y-4 group-hover:translate-y-0
                      opacity-0 group-hover:opacity-100 transition-all duration-300">
        <div className="text-[11px] tracking-[.16em] uppercase gold">{p.category || "Project"} · {p.location || "—"}</div>
        <div className="text-xl md:text-2xl cream mt-2 leading-tight">{p.title}</div>
      </div>
    </Link>
  );
}
